import * as React from 'react';
import { ReactStripeElements, injectStripe } from 'react-stripe-elements';
import AdminUpgradePlanFlowEnd from './AdminUpgradePlanFlowEnd';
import AdminUpgradePlanForms from './AdminUpgradePlanForms';
import AdminUpgradePlanOrganizationInfoForm from './AdminUpgradePlanOrganizationInfoForm';
import AdminUpgradePlanPaymentForm from './AdminUpgradePlanPaymentForm';
import AdminUpgradePlanQuoteForm from './AdminUpgradePlanQuoteForm';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
// @ts-ignore
import { createSelfServeHelpRequest } from 'admin/actions/AdminUpgradePlanActions';
import Input from 'components/Input';
import { largeOrgThreshold, baseTrackingPrefix } from 'constants/SelfServeConstants';
import { OrganizationType, SelfServeHelpScreenSource } from 'globalTypes';
import { priceInCents } from 'utils/selfServeHelpers';
import ToastActions from 'actions/ToastActions';
import { convertSelfServeLead } from 'admin/actions/AdminUpgradePlanActions';
import EventTracking from 'lib/EventTracking'

import '../styles/AdminUpgradePlanContainer.scss';
export type UpgradePlanTabKey =
  | 'org-info'
  | 'quote'
  | 'payment'
  | 'purchase-complete'
  | 'contact-sales';

type PaymentMethod = 'card';

export type UpgradePlanData = {
  country?: string;
  state?: string;
  city?: string;
  orgUuid?: string;
  initialOrgUuid?: string;
  displayOrgStats?: boolean;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  role?: string;
  phoneNumber?: string;
  requesterEmail?: string;
  orgName?: string;
  orgAddress?: string;
  orgType?: OrganizationType;
  startDate?: string;
  endDate?: string;
  price?: string;
  taxExempt?: boolean;
  tax?: string,
  billingFirstName?: string;
  billingLastName?: string;
  billingEmail?: string;
  useRequesterAsBillingContact?: boolean;
  adminVerificationUrl?: string;
  purchaseSuccessful: boolean;
};

export type UpgradePlanQuote = {
  uuid: string;
  studentsCount: number;
  teachersCount: number;
  startDate: string;
  endDate: string;
  taxExempt: boolean;
  price: string;
  tax: string;
  total: string;
  expirationDate: string;
  shareToken: string;
  organization: Organization;
  organizationAddress: OrganizationAddress;
};

export type UpgradePlanTab = {
  tabKey: UpgradePlanTabKey;
  component: React.ComponentClass<any>;
  nextStep?: () => UpgradePlanTabKey;
  prevStep?: () => UpgradePlanTabKey;
  isComplete: boolean;
};

type Organization = {
  id: number;
  uuid: string;
  name: string;
  city: string;
  state: string;
};

type OrganizationAddress = {
  latitude?: string;
  longitude?: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};

type Props = {
  orgUuid?: string;
};

type State = {
  upgradePlanData: UpgradePlanData;
  upgradePlanQuote: UpgradePlanQuote;
  currentTabKey: UpgradePlanTabKey;
  paymentMethod: PaymentMethod;
  cardError?: string;
  cardInputCompleted: boolean;
  submittingPayment: boolean;
};

class AdminUpgradePlanContainer extends React.Component<
  Props & ReactStripeElements.InjectedStripeProps,
  State
> {
  constructor(props: Props) {
    super(props);
  }

  state: State = {
    upgradePlanData: {
      country: '',
      state: '',
      city: '',
      orgUuid: '',
      initialOrgUuid: this.props.orgUuid ?? '',
      displayOrgStats: false,
      lat: 0,
      long: 0,
      teachersCount: 0,
      studentsCount: 0,
      comment: '',
      requesterFirstName: '',
      requesterLastName: '',
      role: '',
      phoneNumber: '',
      requesterEmail: '',
      orgName: '',
      orgAddress: '',
      orgType: OrganizationType.School,
      startDate: '',
      endDate: '',
      price: '',
      taxExempt: false,
      tax: '$0.00',
      purchaseSuccessful: false,
    },
    upgradePlanQuote: {
      uuid: '',
      studentsCount: 0,
      teachersCount: 0,
      startDate: '',
      endDate: '',
      taxExempt: false,
      price: '',
      tax: '',
      total: '',
      expirationDate: '',
      shareToken: '',
      organization: {
        id: 0,
        uuid: '',
        name: '',
        city: '',
        state: '',
      },
      organizationAddress: {
        streetAddress: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
      },
    },
    currentTabKey: 'org-info' as UpgradePlanTabKey,
    paymentMethod: 'card' as PaymentMethod,
    cardInputCompleted: false,
    submittingPayment: false,
  };

  async componentDidMount() {
    try {
      await AuthActions.checkLoggedIn();
      this.showOrgStats()
    } catch(e) {
      this.hideOrgStats()
    }
  }

  track = (key: string) => {
    EventTracking.trackKey(`${baseTrackingPrefix}.${key}`);
  }

  showOrgStats = () => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, displayOrgStats: true } })
  }

  hideOrgStats = () => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, displayOrgStats: false } })
  }

  saveAdminUpgradePlanData = (
    fields: UpgradePlanData,
    quote: UpgradePlanQuote,
    onSuccess: Function
  ) => {
    this.setState({
      upgradePlanData: {
        ...this.state.upgradePlanData,
        ...fields,
      },
      upgradePlanQuote: {
        ...this.state.upgradePlanQuote,
        ...quote,
      },
    }, () => {
      onSuccess();
    });
  }

  submitPayment = async (onSuccess: () => void) => {
    this.setState({ submittingPayment: true });
    const { upgradePlanData, upgradePlanQuote } = this.state;
    // TODO: need to update the quote first if different billing
    // details were set
    const {
      orgUuid,
    } = upgradePlanData;
    const {
      uuid: quoteUuid,
      organization,
      total,
    } = upgradePlanQuote;

    const { source, error } = await this.props.stripe!.createSource();
    if (!source) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      console.error('AdminUpgradePlanContainer - problem creating card source');
      this.setState({ submittingPayment: false });
      return;
    }
    return convertSelfServeLead(
      source.id,
      organization.uuid,
      organization.id,
      priceInCents(total),
      quoteUuid
    ).then(resp => {
      if (resp.success) {
        this.track('data.convert_self_serve_lead.success');
        ToastActions.showSuccessToast('Your card has been charged. We’ll be in touch.');
        this.setState({ submittingPayment: false });
        return onSuccess();
      }
      ToastActions.showErrorToast(
        'There was a problem processing your payment. Please try again.'
      );

      let newState = { cardError: undefined, submittingPayment: false };
      if (resp.declineReason) {
        this.track('data.convert_self_serve_lead.failure');
        newState = { cardError: resp.declineReason, ...newState };
      }
      this.setState(newState);
    });
  }

  createHelpRequest = async () => {
    const { upgradePlanData, upgradePlanQuote } = this.state;

    try {
      await createSelfServeHelpRequest({
        requesterPhoneNumber: `+${upgradePlanData.phoneNumber}`!,
        studentsCount: Number(upgradePlanData.studentsCount),
        teachersCount: Number(upgradePlanData.teachersCount),
        organizationUuid: upgradePlanQuote.organization.uuid,
        requesterFirstName: upgradePlanData.requesterFirstName,
        requesterLastName: upgradePlanData.requesterLastName,
        requesterEmail: upgradePlanData.requesterEmail,
        requesterRole: upgradePlanData.role,
        source: SelfServeHelpScreenSource.self_serve
      })
      this.track('data.create_self_serve_help_request.success');
    } catch(e) {
      this.track('data.create_self_serve_help_request.failure');
    }
  }

  onTaxExemptChange = (taxExempt: boolean) => {
    this.setState({ upgradePlanData: { ...this.state.upgradePlanData, taxExempt } });
  };

  render() {
    const {
      upgradePlanData,
      upgradePlanQuote,
      currentTabKey,
      submittingPayment,
      cardInputCompleted,
      cardError,
    } = this.state;

    const tabs = [
      {
        tabKey: 'org-info',
        component: AdminUpgradePlanOrganizationInfoForm,
        prevStep: undefined,
        nextStep: () => {
          const { upgradePlanData } = this.state
          const { studentsCount } = upgradePlanData;
          if (studentsCount && studentsCount > largeOrgThreshold) {
            return 'contact-sales';
          }
          return 'quote';
        },
        isComplete: false,
      },
      {
        tabKey: 'quote',
        component: AdminUpgradePlanQuoteForm,
        prevStep: () => 'org-info',
        nextStep: () => 'quote-payment',
        isComplete: false,
      },
      {
        tabKey: 'quote-payment',
        component: AdminUpgradePlanPaymentForm,
        prevStep: () => 'quote',
        nextStep: () => 'purchase-complete',
        isComplete: false,
      },
      {
        tabKey: 'contact-sales',
        component: AdminUpgradePlanFlowEnd,
        prevStep: undefined,
        nextStep: undefined,
        isComplete: true,
      },
      {
        tabKey: 'purchase-complete',
        component: AdminUpgradePlanFlowEnd,
        prevStep: () => 'quote-payment',
        nextStep: undefined,
        isComplete: true,
      },
    ] as UpgradePlanTab[];
    const currentTab = tabs.find(tab => tab.tabKey === currentTabKey);

    return (
      <div className="AdminUpgradePlanContainer">
        <AdminUpgradePlanForms
          tabs={tabs}
          currentTab={currentTab}
          fields={upgradePlanData}
          quote={upgradePlanQuote}
          cardError={cardError}
          onNoOrgStats={() => this.hideOrgStats()}
          onCardInputCompleted={() => this.setState({ cardInputCompleted: true })}
          onPersistState={this.saveAdminUpgradePlanData}
          onTaxExemptChange={this.onTaxExemptChange}
          onAcceptPlanTerms={this.submitPayment}
          onContactSales={this.createHelpRequest}
          submittingPayment={submittingPayment}
          cardInputCompleted={cardInputCompleted}
        />
      </div>
    );
  }
}

export { AdminUpgradePlanContainer };
export default injectStripe(AdminUpgradePlanContainer);
